<script setup lang="ts">
import { __ } from '@@/bits/intl'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

withDefaults(
  defineProps<{
    orientation?: 'horizontal' | 'vertical'
  }>(),
  {
    orientation: 'horizontal',
  },
)

const emit = defineEmits(['reset-viewport', 'zoom-out', 'zoom-in'])

const surfaceStore = useSurfaceStore()
const { colorScheme, dir, isScreenshotMode, isSubmissionRequest } = storeToRefs(surfaceStore)

const surfaceMapStore = useSurfaceMapStore()
const { isPreviewPanelShown } = storeToRefs(surfaceMapStore)
const { toggleMapPreviewPanel } = surfaceMapStore

const buttonGroup = computed<
  { label: string; iconName: string; testId?: string; onClick: () => void; isActive?: boolean }[][]
>(() => {
  return [
    [
      {
        label: __('Reset viewport'),
        iconName: 'fullscreen',
        onClick: (): void => {
          emit('reset-viewport')
        },
      },
      {
        label: __('Zoom out'),
        iconName: 'zoom_out_thin',
        onClick: (): void => {
          emit('zoom-out')
        },
      },
      {
        label: __('Zoom in'),
        iconName: 'zoom_in_thin',
        onClick: (): void => {
          emit('zoom-in')
        },
      },
    ],
    isSubmissionRequest.value
      ? []
      : [
          {
            label: __('Show post list'),
            iconName: dir.value === 'rtl' ? 'bulleted_list_ltr' : 'bulleted_list_rtl',
            onClick: toggleMapPreviewPanel,
            isActive: isPreviewPanelShown.value,
            testId: 'surfaceMapShowPostListButton',
          },
        ],
  ]
})
</script>

<template>
  <!-- Use v-show to hide this because Google is smart and will insert a fallback control -->
  <!-- if they don't see any control buttons on the map. -->
  <div
    v-show="!isScreenshotMode"
    :class="['flex items-center', orientation === 'vertical' ? 'flex-col space-y-3 w-8' : 'flex-row space-s-3 h-8']"
  >
    <OzBox
      v-for="(buttons, i) in buttonGroup"
      :key="i"
      :class="[
        'flex',
        orientation === 'vertical' ? 'flex-col w-full' : 'flex-row h-full',
        buttons.length > 1 && [orientation === 'vertical' ? 'space-y-1 py-1.5' : 'space-s-1 px-1.5'],
      ]"
      :color="OzBoxColors.PrimaryBlur"
      :dark-mode="colorScheme === 'dark'"
      :radius="12"
    >
      <OzBaseButton
        v-for="button in buttons"
        :key="button.iconName"
        :class="[
          'flex items-center justify-center',
          'rounded-xl',
          'focus:outline-none',
          orientation === 'vertical' ? 'px-0 py-1.5' : 'px-1.5 py-0',
          'bg-transparent',
          colorScheme === 'light' && [
            button.isActive ? 'text-grape-500' : 'text-dark-text-200',
            'hover-hover:hover:text-dark-text-100',
            'hover-hover:active:text-grape-500',
            'focus-visible:text-grape-500',
          ],
          colorScheme === 'dark' && [
            button.isActive ? 'text-canary-500' : 'text-light-text-200',
            'hover-hover:hover:text-light-text-100',
            'hover-hover:active:text-canary-500',
            'focus-visible:text-canary-500',
          ],
        ]"
        :aria-label="button.label"
        :data-testid="button.testId"
        @click="button.onClick"
      >
        <template>
          <OzIcon :name="button.iconName" :size="20" />
        </template>
      </OzBaseButton>
    </OzBox>
  </div>
</template>
